import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'

import {Icon} from '@vio/atlas/Icon'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {Tag} from '@vio/atlas/Tag'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import {cssTheme} from '@vio/atlas-tokens'

import {
  getHotelBadges,
  getHotelCity,
  getHotelStarRating
} from '../../../modules/sapiSearch/selectors'
import {getIsFromGha} from '../../../utils/trafficSource'
import {
  BadgeIconMap,
  HotelBadgeMessages,
  HotelBadgeTooltipMessages
} from './constants'

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${cssTheme.layout.spacing.s300};
  row-gap: ${cssTheme.layout.spacing.s200};
  column-gap: ${cssTheme.layout.spacing.s300};
`

interface Props {
  hotelId: string
}

export const HotelBadges = ({hotelId}: Props) => {
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()

  const hotelBadges = useSelector(state => getHotelBadges(state, hotelId))
  const rating = useSelector(state => getHotelStarRating(state, hotelId))
  const city = useSelector(state => getHotelCity(state, hotelId))
  const isGHA = useSelector(getIsFromGha)

  const badges = useMemo(() => {
    if (isGHA) return hotelBadges.slice(0, 1)
    return hotelBadges
  }, [hotelBadges, isGHA])

  if (badges.length === 0) return null

  if (isMobile) {
    return (
      <Wrapper>
        {badges.map(badge => (
          <Tag
            key={badge}
            dataId={`HotelBadge-${badge}`}
            size="sm"
            variant="neutralOutline"
            iconStart={<Icon size="sm" name={BadgeIconMap[badge]} />}
          >
            {formatMessage(HotelBadgeMessages[badge], {rating})}
          </Tag>
        ))}
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {badges.map(badge => (
        <Tooltip
          key={badge}
          content={formatMessage(HotelBadgeTooltipMessages[badge], {
            city,
            rating
          })}
          placement={TooltipPlacement.BottomStart}
        >
          <Tag
            dataId={`HotelBadge-${badge}`}
            size="sm"
            variant="neutralOutline"
            iconStart={<Icon size="sm" name={BadgeIconMap[badge]} />}
          >
            {formatMessage(HotelBadgeMessages[badge], {rating})}
          </Tag>
        </Tooltip>
      ))}
    </Wrapper>
  )
}
